export const AUTH_TOKEN_STORAGE_KEY = "childplus.auth.token";

export interface ApiErrorPayload {
  error?: {
    code?: string;
    message?: string;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions extends RequestInit {
  authenticated?: boolean;
}

const DEFAULT_API_BASE_URL = "/api/v1";

export function getApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL as string | undefined;
  return normalizeBaseUrl(configured ?? DEFAULT_API_BASE_URL);
}

export function getAuthToken(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
}

export function setAuthToken(token: string): void {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
}

export function clearAuthToken(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
}

export async function apiGet<T>(path: string): Promise<T> {
  return apiRequest<T>(path);
}

export async function apiPost<T>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  return apiRequest<T>(path, {
    method: "POST",
    body: body === undefined ? undefined : JSON.stringify(body),
    ...options
  });
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return apiRequest<T>(path, {
    method: "PUT",
    body: body === undefined ? undefined : JSON.stringify(body)
  });
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return apiRequest<T>(path, {
    method: "PATCH",
    body: body === undefined ? undefined : JSON.stringify(body)
  });
}

export async function apiDelete(path: string): Promise<void> {
  await apiRequest<void>(path, { method: "DELETE" });
}

export async function apiPostBinary<T>(path: string, body: Blob | ArrayBuffer): Promise<T> {
  return apiRequest<T>(path, {
    method: "POST",
    body,
    headers: { "Content-Type": "application/octet-stream" }
  });
}

export async function apiDownloadBlob(path: string): Promise<Blob> {
  const response = await authorizedFetch(path);
  if (!response.ok) {
    const payload = (await readResponse(response)) as ApiErrorPayload;
    handleUnauthorized(response);
    throw new ApiError(
      payload.error?.message ?? response.statusText ?? "下载失败",
      response.status,
      payload.error?.code,
    );
  }
  return response.blob();
}

async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { authenticated = true, headers, ...requestOptions } = options;
  const requestHeaders = new Headers(headers);

  if (requestOptions.body !== undefined && !requestHeaders.has("Content-Type")) {
    requestHeaders.set("Content-Type", "application/json");
  }

  if (authenticated) {
    const token = getAuthToken();
    if (token) {
      requestHeaders.set("Authorization", `Bearer ${token}`);
    }
  }

  const response = await fetch(buildUrl(path), {
    ...requestOptions,
    headers: requestHeaders
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = await readResponse(response);

  if (!response.ok) {
    const errorPayload = payload as ApiErrorPayload;
    handleUnauthorized(response);
    throw new ApiError(
      errorPayload.error?.message ?? response.statusText ?? "请求失败",
      response.status,
      errorPayload.error?.code,
    );
  }

  return payload as T;
}

async function authorizedFetch(path: string): Promise<Response> {
  const headers = new Headers();
  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return fetch(buildUrl(path), { headers });
}

function handleUnauthorized(response: Response): void {
  if (response.status !== 401) return;
  clearAuthToken();
  notifyUnauthorized();
}

async function readResponse(response: Response): Promise<unknown> {
  const contentType = response.headers.get("Content-Type") ?? "";
  if (contentType.includes("application/json")) {
    return response.json();
  }
  const text = await response.text();
  if (!text) {
    return undefined;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function buildUrl(path: string): string {
  if (/^https?:\/\//.test(path)) {
    return path;
  }
  const baseUrl = getApiBaseUrl();
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}

function normalizeBaseUrl(value: string): string {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

function notifyUnauthorized(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new Event("childplus:unauthorized"));
}
